import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

// Picks a clean y-axis ceiling + tick step that hugs the data closely.
// Same helper used by the other SVG charts (VoteBarChart etc).
function niceScale(maxValue, maxTicks = 6) {
  if (!maxValue || maxValue <= 0) return { max: 4, step: 1 };
  const candidates = [];
  for (let p = -2; p <= 4; p++) {
    const mag = Math.pow(10, p);
    [1, 2, 2.5, 5].forEach((m) => candidates.push(m * mag));
  }
  candidates.sort((a, b) => a - b);
  for (const step of candidates) {
    const max = Math.ceil(maxValue / step - 1e-9) * step;
    if (max / step <= maxTicks) return { max, step };
  }
  const step = candidates[candidates.length - 1];
  return { max: Math.ceil(maxValue / step) * step, step };
}

const ROUND_COUNT = 25; // rounds 0..24
const LAST_ROUND = ROUND_COUNT - 1;

// Categorical palette for up to 20 concurrent player lines. Shared with the
// live leaderboard so each player's dot matches their line.
export const LINE_COLORS = [
  '#34d399', // emerald-400
  '#38bdf8', // sky-400
  '#fbbf24', // amber-400
  '#a78bfa', // violet-400
  '#f472b6', // pink-400
  '#2dd4bf', // teal-400
  '#818cf8', // indigo-400
  '#fb923c', // orange-400
  '#a3e635', // lime-400
  '#22d3ee', // cyan-400
  '#e879f9', // fuchsia-400
  '#fb7185', // rose-400
  '#4ade80', // green-400
  '#60a5fa', // blue-400
  '#facc15', // yellow-400
  '#c084fc', // purple-400
  '#5eead4', // teal-300
  '#94a3b8', // slate-400
  '#f59e0b', // amber-500
  '#0ea5e9', // sky-500
];

// Cumulative value at a (possibly fractional) round, linearly interpolated.
export const valueAt = (values, progress) => {
  const full = Math.floor(progress);
  const base = Number(values[full]) || 0;
  if (progress <= full || full >= LAST_ROUND) return base;
  const next = Number(values[full + 1]) || 0;
  return base + (next - base) * (progress - full);
};

export const initialsFor = (name) =>
  (name || '')
    .split(' ')
    .filter(Boolean)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

// Controlled chart: the parent owns playback (progress 0..24, runActive,
// paused) and hover state so the live leaderboard can share them.
// series: [{ name, team, image, values: number[25] (cumulative), total }]
const PlayerVoteProgressionLineGraph = ({
  series = [],
  progress = LAST_ROUND,
  runActive = false,
  paused = false,
  hovered = null,
  onHover = () => {},
}) => {
  const wrapRef = useRef(null);
  const [dims, setDims] = useState({ w: 720, h: 400 });
  // Once a run has started, the draw-in animation should not replay when the
  // chart settles back to the full view.
  const [played, setPlayed] = useState(false);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    // Use the real box size so the SVG never overflows its flex slot; the
    // wrapper's min-height guards against degenerate measurements.
    const update = () =>
      setDims({ w: Math.max(300, el.clientWidth), h: Math.max(160, el.clientHeight) });
    update();
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(update);
      ro.observe(el);
    } else {
      window.addEventListener('resize', update);
    }
    return () => {
      if (ro) ro.disconnect();
      else window.removeEventListener('resize', update);
    };
  }, []);

  // Remounting the line group replays the draw-in animation when the player
  // set (Top N / datasets) changes.
  const animKey = `${series.length}|${series.map((s) => s.name).join(',')}|${(series[0]?.total ?? 0).toFixed(3)}`;

  useEffect(() => {
    setPlayed(false);
  }, [animKey]);

  useEffect(() => {
    if (runActive) setPlayed(true);
  }, [runActive]);

  const geom = useMemo(() => {
    const padL = 34;
    const padR = 34; // room for the avatar markers at the line ends
    const padT = 14;
    const padB = 26;
    const svgW = dims.w;
    const svgH = dims.h;
    const innerW = svgW - padL - padR;
    const innerH = svgH - padT - padB;

    const maxVal = Math.max(1, ...series.map((s) => Number(s.values[s.values.length - 1]) || 0));
    const { max: yMax, step } = niceScale(maxVal);

    const yFor = (v) => padT + innerH - (Math.max(0, v) / yMax) * innerH;
    const xFor = (r) => padL + (innerW * r) / LAST_ROUND;

    const ticks = [];
    const tickCount = Math.round(yMax / step);
    for (let t = 0; t <= tickCount; t++) {
      const v = t * step;
      ticks.push({ v, y: yFor(v) });
    }

    // Avatars sit at each line's final point; nudge them apart vertically so
    // clustered totals don't stack the photos on top of each other.
    const spacing = Math.min(21, series.length > 1 ? innerH / (series.length - 1) : innerH);
    const targets = series
      .map((s, i) => ({ i, y: yFor(Number(s.values[LAST_ROUND]) || 0) }))
      .sort((a, b) => a.y - b.y);
    let prev = -Infinity;
    for (const t of targets) {
      t.adj = Math.max(t.y, prev + spacing);
      prev = t.adj;
    }
    // If the pass pushed avatars past the baseline, walk back up.
    let nextTop = padT + innerH;
    for (let k = targets.length - 1; k >= 0; k--) {
      targets[k].adj = Math.min(targets[k].adj, nextTop);
      nextTop = targets[k].adj - spacing;
    }
    const endY = {};
    targets.forEach((t) => {
      endY[t.i] = Math.max(12, t.adj);
    });

    return { padL, padR, padT, padB, svgW, svgH, innerH, yMax, yFor, xFor, ticks, endY };
  }, [dims, series]);

  const { padL, padR, padT, svgW, svgH, innerH, yFor, xFor, ticks, endY } = geom;
  const baselineY = yFor(0);
  const lastX = xFor(LAST_ROUND);
  const settled = !runActive && progress >= LAST_ROUND;

  // Path up to the current playback progress (full path when idle).
  const buildPath = (values) => {
    const upto = Math.min(LAST_ROUND, progress);
    const full = Math.floor(upto);
    const pts = [];
    for (let r = 0; r <= full; r++) {
      pts.push(`${xFor(r).toFixed(1)},${yFor(Number(values[r]) || 0).toFixed(1)}`);
    }
    if (upto > full) {
      pts.push(`${xFor(upto).toFixed(1)},${yFor(valueAt(values, upto)).toFixed(1)}`);
    }
    return `M${pts.join(' L')}`;
  };

  // Label every round when wide, thin out on narrower viewports.
  const xLabelStep = dims.w < 480 ? 4 : dims.w < 820 ? 2 : 1;

  const clearHover = () => onHover(null);

  if (!series.length) return null;

  return (
    <div className="h-full flex flex-col">
      <style>{`
        @keyframes vpgDraw { from { stroke-dashoffset: 1; } to { stroke-dashoffset: 0; } }
        .vpg-draw { animation: vpgDraw 0.9s cubic-bezier(0.22, 1, 0.36, 1) backwards; }
        @keyframes vpgFade { from { opacity: 0; } to { opacity: 1; } }
        .vpg-fade { animation: vpgFade 0.35s ease 0.55s backwards; }
      `}</style>

      <div ref={wrapRef} className="relative w-full flex-1 min-h-[300px] md:min-h-[200px] overflow-hidden">
        <svg
          width={svgW}
          height={svgH}
          className="block absolute inset-0"
          role="img"
          aria-label="Cumulative vote progression by round"
          onMouseLeave={clearHover}
        >
          <defs>
            {/* Shared circular clip for the avatar photos (local coords). */}
            <clipPath id="vpg-avatar-clip">
              <circle cx={0} cy={0} r={9} />
            </clipPath>
          </defs>

          {/* Horizontal gridlines + y labels */}
          {ticks.map((t, i) => (
            <g key={i}>
              <line x1={padL} y1={t.y} x2={svgW - padR} y2={t.y} stroke="#1e293b" strokeWidth="1" />
              <text x={padL - 6} y={t.y + 3} textAnchor="end" className="fill-slate-500" style={{ fontSize: 9, fontVariantNumeric: 'tabular-nums' }}>
                {Number.isInteger(t.v) ? t.v : t.v.toFixed(1)}
              </text>
            </g>
          ))}

          {/* Faint vertical guides every 5 rounds */}
          {[5, 10, 15, 20].map((r) => (
            <line key={r} x1={xFor(r)} y1={padT} x2={xFor(r)} y2={padT + innerH} stroke="#1e293b" strokeWidth="1" strokeDasharray="2 4" opacity="0.6" />
          ))}

          {/* Round labels */}
          {Array.from({ length: ROUND_COUNT }, (_, r) => r)
            .filter((r) => r % xLabelStep === 0)
            .map((r) => (
              <text
                key={r}
                x={xFor(r)}
                y={svgH - 8}
                textAnchor="middle"
                className={runActive && r <= progress ? 'fill-slate-300' : 'fill-slate-500'}
                style={{ fontSize: 8.5, fontVariantNumeric: 'tabular-nums' }}
              >
                {r}
              </text>
            ))}

          {/* Player lines (keyed group so draw-in replays on change) */}
          <g key={animKey}>
            {series.map((s, i) => {
              const color = LINE_COLORS[i % LINE_COLORS.length];
              const dim = hovered != null && hovered !== i;
              return (
                <path
                  key={s.name}
                  d={buildPath(s.values)}
                  fill="none"
                  stroke={color}
                  strokeWidth={hovered === i ? 2.5 : 1.75}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  pathLength="1"
                  strokeDasharray="1"
                  className={played ? undefined : 'vpg-draw'}
                  style={{ animationDelay: `${Math.min(i * 60, 600)}ms`, opacity: dim ? 0.12 : 1, transition: 'opacity 0.15s, stroke-width 0.15s' }}
                />
              );
            })}
          </g>

          {/* Point markers for the hovered line */}
          {hovered != null && series[hovered] && !runActive && (
            <g pointerEvents="none">
              {series[hovered].values.map((v, r) => (
                <circle key={r} cx={xFor(r)} cy={yFor(Number(v) || 0)} r={2.4} fill={LINE_COLORS[hovered % LINE_COLORS.length]} stroke="#0a1120" strokeWidth="1" />
              ))}
            </g>
          )}

          {/* Avatar markers: ride the line tip during playback, settle at the
              nudged end-of-line rail when idle. */}
          {series.map((s, i) => {
            const color = LINE_COLORS[i % LINE_COLORS.length];
            const dim = hovered != null && hovered !== i;
            const tipX = xFor(Math.min(LAST_ROUND, progress));
            const tipY = yFor(valueAt(s.values, progress));
            const cx = settled ? lastX + 15 : tipX;
            const cy = settled ? endY[i] : tipY;
            const endPointY = yFor(Number(s.values[LAST_ROUND]) || 0);
            return (
              <g
                key={s.name}
                className={played ? undefined : 'vpg-fade'}
                style={{ opacity: dim ? 0.15 : 1, transition: 'opacity 0.15s', cursor: 'default' }}
                onMouseEnter={() => onHover(i)}
                onMouseLeave={clearHover}
              >
                <title>{`${s.name} — ${Number(s.total).toFixed(2)} projected votes`}</title>
                {/* Connector from the line end to the (possibly nudged) avatar */}
                {settled && (
                  <line x1={lastX} y1={endPointY} x2={cx - 10} y2={cy} stroke={color} strokeWidth="1" opacity="0.5" />
                )}
                <g
                  style={{
                    transform: `translate(${cx}px, ${cy}px)`,
                    transition: runActive && !paused ? 'none' : 'transform 0.5s cubic-bezier(0.22, 1, 0.36, 1)',
                  }}
                >
                  <circle cx={0} cy={0} r={10} fill="#0a1120" stroke={color} strokeWidth="1.5" />
                  {s.image ? (
                    <image
                      href={s.image}
                      x={-9}
                      y={-9}
                      width={18}
                      height={18}
                      clipPath="url(#vpg-avatar-clip)"
                      preserveAspectRatio="xMidYMid slice"
                    />
                  ) : (
                    <text x={0} y={2.5} textAnchor="middle" className="fill-slate-300" style={{ fontSize: 7, fontWeight: 700 }}>
                      {initialsFor(s.name)}
                    </text>
                  )}
                </g>
              </g>
            );
          })}

          {/* Live round indicator during playback or when scrubbed to a round */}
          {(runActive || progress < LAST_ROUND) && (
            <text
              x={padL + 8}
              y={padT + 20}
              className="fill-slate-300"
              style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.14em', fontVariantNumeric: 'tabular-nums' }}
            >
              ROUND {Math.min(LAST_ROUND, Math.floor(progress))}
              {paused ? '  ·  PAUSED' : ''}
            </text>
          )}

          {/* Invisible fat strokes so lines are easy to hover; drawn last to sit on top */}
          {!runActive &&
            series.map((s, i) => (
              <path
                key={s.name}
                d={buildPath(s.values)}
                fill="none"
                stroke="rgba(0,0,0,0)"
                strokeWidth="11"
                pointerEvents="stroke"
                onMouseEnter={() => onHover(i)}
                onMouseLeave={clearHover}
              >
                <title>{`${s.name} — ${Number(s.total).toFixed(2)} projected votes`}</title>
              </path>
            ))}

          {/* Baseline */}
          <line x1={padL} y1={baselineY} x2={svgW - padR} y2={baselineY} stroke="#334155" strokeWidth="1.25" />
        </svg>
      </div>
    </div>
  );
};

export default PlayerVoteProgressionLineGraph;
